/**
 * <brand-newsletter-form> — subscribes to the newsletter without ever reloading the page.
 * Wraps a Shopify `{% form 'customer' %}` (footer, newsletter section, welcome popup).
 *
 * Shopify's captcha owns the submission of these forms (inline `captcha-bootstrap` script plus
 * `storefront-forms-hcaptcha`, read on 2026-10-01): on submit it cancels the event, asks hCaptcha
 * for a token, writes it in a hidden `h-captcha-response` field, then calls the form's own
 * `submit()` method, which it read from the form when it bound it (first focus or change inside
 * the form). A scripted POST sent without that token is rejected. So the request is not sent
 * around the captcha but at the end of it: this element replaces the form's `submit()` method
 * before the captcha binds it, and Shopify hands the tokenised form back to `#send()`.
 *
 * Shopify answers the POST with a redirect, and the final URL carries the outcome:
 * `customer_posted=true` on success, `/challenge` when it wants a full-page verification. That
 * last case cannot be completed with fetch, so the form is then submitted natively: a reload is
 * the only way left to subscribe the visitor.
 *
 * Messages are server-rendered into `<template data-newsletter-message>` (snippets/
 * brand-newsletter-state.liquid) and moved into `[data-newsletter-state]`, so every text stays
 * translated and editable in the theme.
 *
 * Emits `newsletter:subscribed` once the subscription is confirmed, `newsletter:failed` when the
 * attempt ends in an error. Both bubble.
 */
const CAPTCHA_BOUND = 'data-hcaptcha-bound';
// How long a submission waits for a captcha that is still loading before giving up.
const CAPTCHA_TIMEOUT = 10000;

/**
 * @param {Response} response
 * @returns {'success' | 'challenge' | 'error'}
 */
function outcomeOf(response) {
  const url = new URL(response.url);
  if (url.searchParams.get('customer_posted') === 'true') return 'success';
  if (url.pathname.includes('/challenge')) return 'challenge';
  return 'error';
}

class BrandNewsletterForm extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;
  // True from the click to the outcome, captcha wait included: a second click starts nothing.
  #busy = false;

  connectedCallback() {
    const form = this.querySelector('form');
    if (!form || form === this.#form) return;
    this.#form = form;
    // A form the captcha has already bound keeps the native method it read: nothing can be
    // taken over anymore, it submits natively. Only happens if a field is focused before this
    // module has run.
    if (!form.hasAttribute(CAPTCHA_BOUND)) form.submit = () => this.#send();
    form.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
    this.#form = null;
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = this.#form;
    if (!form) return;
    // Bound: Shopify's own listener takes over and ends in `form.submit()`, hence `#send()`.
    if (form.hasAttribute(CAPTCHA_BOUND)) return;
    event.preventDefault();
    // Captcha requested but still loading: Shopify drops such a submission, the visitor would
    // have to click again. It is replayed as soon as the captcha is ready.
    if (form.dataset.cptcha) this.#submitOnceBound(event.submitter);
    else this.#send();
  };

  /** @param {HTMLElement | null} submitter */
  #submitOnceBound(submitter) {
    const form = this.#form;
    if (!form || this.#busy) return;
    this.#setBusy(true);
    this.#show(null);
    const observer = new MutationObserver(() => {
      if (!form.hasAttribute(CAPTCHA_BOUND)) return;
      observer.disconnect();
      window.clearTimeout(timer);
      // Handed over to the captcha, which shows its own progress.
      this.#setBusy(false);
      form.requestSubmit(submitter);
    });
    const timer = window.setTimeout(() => {
      observer.disconnect();
      this.#settle('error');
    }, CAPTCHA_TIMEOUT);
    observer.observe(form, { attributes: true, attributeFilter: [CAPTCHA_BOUND] });
  }

  async #send() {
    const form = this.#form;
    if (!form || this.#busy) return;
    this.#setBusy(true);
    this.#show(null);

    /** @type {'success' | 'challenge' | 'error'} */
    let outcome = 'error';
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        // Same encoding as the native submission this request stands in for.
        body: new URLSearchParams(new FormData(form)),
        headers: { Accept: 'text/html' },
      });
      outcome = outcomeOf(response);
    } catch {
      outcome = 'error';
    }

    if (outcome === 'challenge') {
      HTMLFormElement.prototype.submit.call(form);
      return;
    }

    this.#settle(outcome);
  }

  /** @param {'success' | 'error'} outcome */
  #settle(outcome) {
    this.#setBusy(false);
    if (outcome === 'success') this.#form?.reset();
    // Announced before the message is shown: a listener that reopens its dialog moves the focus,
    // and the focus set by `#show()` on the email field must come last.
    this.dispatchEvent(new CustomEvent(outcome === 'success' ? 'newsletter:subscribed' : 'newsletter:failed', { bubbles: true }));
    this.#show(outcome);
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    const form = this.#form;
    if (!form) return;
    form.toggleAttribute('aria-busy', busy);
    form.querySelectorAll('button[type="submit"]').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = busy;
    });
  }

  /** @param {'success' | 'error' | null} kind message to show, null to clear the current one */
  #show(kind) {
    const state = this.querySelector('[data-newsletter-state]');
    const template = kind ? this.querySelector(`template[data-newsletter-message="${kind}"]`) : null;
    state?.replaceChildren(...(template instanceof HTMLTemplateElement ? [template.content.cloneNode(true)] : []));

    const email = this.#form?.querySelector('input[type="email"]');
    if (!email) return;
    const message = state?.firstElementChild;
    if (kind === 'error') email.setAttribute('aria-invalid', 'true');
    else email.removeAttribute('aria-invalid');
    if (message?.id) email.setAttribute('aria-describedby', message.id);
    else email.removeAttribute('aria-describedby');
    if (kind === 'error' && email instanceof HTMLElement) email.focus({ preventScroll: true });
  }
}

if (!customElements.get('brand-newsletter-form')) {
  customElements.define('brand-newsletter-form', BrandNewsletterForm);
}
